"use client";

import { useChat, type UseChatHelpers } from "@ai-sdk/react";
import {
  DefaultChatTransport,
  lastAssistantMessageIsCompleteWithToolCalls,
  type UIMessage,
} from "ai";
import { useCallback, useMemo, useRef, useState } from "react";
import { trimDslResultForAgent } from "@/lib/agent/agent-dataset-cache";
import { buildAgentChartToolResult } from "@/lib/agent/agent-chart-tool-result";
import { validateAgentChartInput } from "@/lib/agent/validate-chart-spec";
import { usePlatform } from "@/platform/PlatformProvider";
import { resolveLlmUrl } from "@/platform/resolve-url";
import { vybeTraderTerminalDslEngine } from "@/lib/terminal/engine";
import { getVybeToken } from "@/lib/vybe-auth";
import { useSettingsStore } from "@/lib/settings";
import {
  useSubscribedSymbolsStore,
  useVybeTraderClient,
} from "@/vybe-trader-client";
import {
  validatePlaceOrderInput,
  type AgentPlaceOrderInput,
} from "@/lib/agent/validate-place-order";
import type { AgentOrderConsentRequest } from "@/components/dialogs/AgentOrderConsentDialog";

/** Extra surface the agent chat exposes for the order-consent gate. */
export interface AgentOrderConsentControls {
  /** Order awaiting the user's decision, or null when nothing is pending. */
  pendingOrderConsent: AgentOrderConsentRequest | null;
  /** User approved: proceed to send the order. */
  approveOrderConsent: () => void;
  /** User rejected (or dismissed): fail the tool call closed. */
  rejectOrderConsent: () => void;
}

export type VybeAgentChat = UseChatHelpers<UIMessage> &
  AgentOrderConsentControls;

// The blocked-write policy is shared with the desktop MCP bridge so the two
// machine callers can never drift apart — see lib/agent/dsl-write-guard.
import {
  AGENT_BLOCKED_CATALOG_PATHS,
  AGENT_BLOCKED_DSL,
  AGENT_BLOCKED_DSL_MESSAGE,
} from "@/lib/agent/dsl-write-guard";

/** Streaming Vybe Agent chat with client-side DSL, chart, and gated order tools. */
export function useVybeAgentChat(): VybeAgentChat {
  const config = usePlatform();
  const toolSinkRef = useRef<UseChatHelpers<UIMessage>["addToolOutput"] | null>(
    null,
  );
  const { client } = useVybeTraderClient();

  // Consent state: the order awaiting a decision, plus the resolver the dialog
  // invokes. Bridges the async place_order tool call to the modal.
  const [pendingOrderConsent, setPendingOrderConsent] =
    useState<AgentOrderConsentRequest | null>(null);
  const consentResolverRef = useRef<((approved: boolean) => void) | null>(null);

  // All tool executions are chained on this queue so their addToolOutput calls land
  // strictly one at a time. When an assistant turn carries many parallel tool calls,
  // concurrent output resolutions can each trip sendAutomaticallyWhen and fire
  // duplicate re-POSTs — two interleaved assistant streams in the same chat.
  const execQueueRef = useRef<Promise<void>>(Promise.resolve());
  const enqueueToolRun = useCallback((run: () => Promise<void>) => {
    execQueueRef.current = execQueueRef.current.then(run, run);
  }, []);

  // Serial execution means one hung DSL call would block every queued call behind it
  // (and the whole reason-act loop) forever. Bound each execution instead.
  const DSL_TIMEOUT_MS = 30_000;
  const withTimeout = useCallback(<T,>(p: Promise<T>, label: string): Promise<T> => {
    return new Promise<T>((resolve, reject) => {
      const t = setTimeout(
        () => reject(new Error(`${label} timed out after ${DSL_TIMEOUT_MS / 1000}s — the call may still complete in the terminal; do not blindly retry.`)),
        DSL_TIMEOUT_MS,
      );
      p.then(
        (v) => { clearTimeout(t); resolve(v); },
        (e) => { clearTimeout(t); reject(e); },
      );
    });
  }, []);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: resolveLlmUrl("/api/agent/chat", config),
        // The agent is gated behind a Vybe Trader login — attach the session token.
        headers: (): Record<string, string> => {
          const token = getVybeToken();
          return token ? { Authorization: `Bearer ${token}` } : {};
        },
        body: async () => {
          await import("@/lib/terminal/engine/modules");
          const body: {
            dslCatalog: ReturnType<typeof vybeTraderTerminalDslEngine.list>;
            agentTradingConsent: boolean;
          } = {
            dslCatalog: vybeTraderTerminalDslEngine
              .list()
              .filter((fn) => !AGENT_BLOCKED_CATALOG_PATHS.has(fn.path)),
            // Fail closed on the server: place_order is only exposed when this is true.
            agentTradingConsent:
              useSettingsStore.getState().getSetting("agentTradingConsent"),
          };

          return body;
        },
      }),
    [config],
  );

  // Runs the order-consent gate for a validated place_order call. Resolves true to
  // proceed, false to fail closed. Persisted consent skips the dialog.
  const requestOrderConsent = useCallback(
    (order: AgentOrderConsentRequest): Promise<boolean> => {
      const consented =
        useSettingsStore.getState().getSetting("agentTradingConsent");
      if (consented) {
        return Promise.resolve(true);
      }
      return new Promise<boolean>((resolve) => {
        consentResolverRef.current = resolve;
        setPendingOrderConsent(order);
      });
    },
    [],
  );

  const settleConsent = useCallback((approved: boolean) => {
    setPendingOrderConsent(null);
    const resolve = consentResolverRef.current;
    consentResolverRef.current = null;
    resolve?.(approved);
  }, []);

  const approveOrderConsent = useCallback(
    () => settleConsent(true),
    [settleConsent],
  );
  const rejectOrderConsent = useCallback(
    () => settleConsent(false),
    [settleConsent],
  );

  const chat = useChat({
    transport,
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    async onToolCall({ toolCall }) {
      if (toolCall.dynamic) return;

      const sink = toolSinkRef.current;
      if (!sink) return;

      if (toolCall.toolName === "vybe_dsl") {
        const rawInput = toolCall.input as { call?: unknown } | undefined;
        const call =
          typeof rawInput?.call === "string" ? rawInput.call.trim() : "";

        if (!call) {
          void sink({
            tool: "vybe_dsl",
            toolCallId: toolCall.toolCallId,
            state: "output-error",
            errorText:
              "Missing vybe_dsl.call — provide one DSL expression string.",
          });
          return;
        }

        if (AGENT_BLOCKED_DSL.test(call)) {
          void sink({
            tool: "vybe_dsl",
            toolCallId: toolCall.toolCallId,
            state: "output-error",
            errorText: AGENT_BLOCKED_DSL_MESSAGE,
          });
          return;
        }

        enqueueToolRun(async () => {
          try {
            const result = await withTimeout(
              vybeTraderTerminalDslEngine.execute(call),
              call,
            );
            const output =
              result.status === "ok"
                ? { ...result, value: trimDslResultForAgent(result.value) }
                : result;
            await sink({
              tool: "vybe_dsl",
              toolCallId: toolCall.toolCallId,
              output,
            });
          } catch (e) {
            await sink({
              tool: "vybe_dsl",
              toolCallId: toolCall.toolCallId,
              state: "output-error",
              errorText: e instanceof Error ? e.message : String(e),
            });
          }
        });
        return;
      }

      if (toolCall.toolName === "agent_chart") {
        const validated = validateAgentChartInput(toolCall.input);
        if (!validated.ok) {
          void sink({
            tool: "agent_chart",
            toolCallId: toolCall.toolCallId,
            state: "output-error",
            errorText: validated.error,
          });
          return;
        }

        enqueueToolRun(async () => {
          await sink({
            tool: "agent_chart",
            toolCallId: toolCall.toolCallId,
            output: buildAgentChartToolResult(validated.spec),
          });
        });
        return;
      }

      if (toolCall.toolName === "place_order") {
        const validated = validatePlaceOrderInput(toolCall.input);
        if (!validated.ok) {
          void sink({
            tool: "place_order",
            toolCallId: toolCall.toolCallId,
            state: "output-error",
            errorText: validated.error,
          });
          return;
        }

        enqueueToolRun(async () => {
          try {
            const order = validated.order;
            const approved = await requestOrderConsent(order);
            if (!approved) {
              await sink({
                tool: "place_order",
                toolCallId: toolCall.toolCallId,
                state: "output-error",
                errorText: "Order rejected by user — no order was sent.",
              });
              return;
            }

            const sent = await executeAgentOrder(order, client);
            await sink({
              tool: "place_order",
              toolCallId: toolCall.toolCallId,
              output: sent,
            });
          } catch (e) {
            await sink({
              tool: "place_order",
              toolCallId: toolCall.toolCallId,
              state: "output-error",
              errorText: e instanceof Error ? e.message : String(e),
            });
          }
        });
      }
    },
  });

  toolSinkRef.current = chat.addToolOutput;

  return {
    ...chat,
    pendingOrderConsent,
    approveOrderConsent,
    rejectOrderConsent,
  };
}

/**
 * Routes an agent order through the SAME direct terminal→gateway path the manual
 * ticket uses (client.sendOrder). Market execution: BUY fills at ask, SELL at bid.
 */
async function executeAgentOrder(
  order: AgentPlaceOrderInput,
  client: ReturnType<typeof useVybeTraderClient>["client"],
): Promise<{
  status: "sent";
  action: string;
  symbol: string;
  volume: number;
  price: number;
}> {
  if (!client || !client.isConnected()) {
    throw new Error(
      "Not connected to the trading account — cannot place order.",
    );
  }

  const entry = useSubscribedSymbolsStore
    .getState()
    .byCurrency.get(order.symbol);
  const quote = entry?.quote ?? null;
  const bid = quote?.bid ?? 0;
  const ask = quote?.ask ?? 0;
  if (!quote || bid === 0 || ask === 0) {
    throw new Error(
      `No live quote for ${order.symbol} — cannot price a market order.`,
    );
  }

  const isBuy = order.action === "BUY";
  const price = isBuy ? ask : bid;

  const orderData = {
    Symbol: order.symbol,
    Lots: order.volume,
    OrderType: isBuy ? 0 : 1,
    Price: price,
    StopLoss: order.stopLoss ?? 0,
    TakeProfit: order.takeProfit ?? 0,
    Slippage: 100,
    Comment: "Vybe Agent order",
  };

  await client.sendOrder(orderData);

  return {
    status: "sent",
    action: order.action,
    symbol: order.symbol,
    volume: order.volume,
    price,
  };
}
